import { getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import {
  IPActionButton,
  IPCommandButton,
  IPDefaultButton,
  IPMessageBar,
  IPMessageBarButton,
  IPMessageBarType,
  IPText,
} from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IEvidenceTaskLinkItem } from 'components/AuditCriteria/EvidenceTaskLinks';
import { editIcon, globalStackTokensGapMedium } from 'globalStyles';
import { AuditEntityKey, auditEntityKeyString } from 'models/auditCriteria';
import { AuthSchemaLineOperation } from 'models/auth/authSchemaLine';
import { EntityTypes } from 'models/entity';
import KPI, { KPITypes } from 'models/kpi/kpi';
import KPIData from 'models/kpi/kpiData';
import ResourceLink from 'models/resourceLink';
import { getFormVersionConflictCode, getFormVersionConflictText } from 'components/Tasks/Forms/formVersionConflictText';
import Task from 'models/tasks/task';
import TaskType from 'models/tasks/taskType';
import TaskTypeFormElement, { TaskTypeFieldType } from 'models/tasks/taskTypeFormElement';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetLinksForId } from 'services/Api/linkService';
import { apiGetTaskTypeFormAndData, apiUpgradeTaskFormVersion } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { FeatureTypes, hasUserDataPermission, hasUserFeature } from 'services/Auth/featurePermissions';
import AppError from 'utils/appError';
import { getHtmlJSX } from 'utils/html';
import { sortOnNumber } from 'utils/sorting';
import { isEmpty } from 'utils/string';
import { canUpdateTaskField, hasTaskUpdatePermission, TaskFieldTypes } from '../TaskAuthHelper';
import { TaskTagList } from '../TaskTagList';
import { getTaskViewModalStackStylesFormMode } from '../View/TaskRenderHelpers';
import TaskTypeFormModal from './TaskTypeFormModal';
import TaskTypeFormRenderer from './TaskTypeFormRenderer';

interface ITaskTypeFormProps {
  isOpen: boolean;
  task: Task;
  links: ResourceLink[];
  compact: boolean;
  compactTaskView?: boolean;
  readonly: boolean;
  showErrors: string | undefined; //when this is changed, the form is validated, except when readonly is true
  loadFormAndData?: (accessToken: string) => Promise<TaskTypeFormElement[]>;
  formMode?: boolean;
  hideEmptyFormText?: boolean;
  allowEdit?: boolean;
  // Shown instead of the plain "form is empty" line when this form has nothing to render, so the
  // caller can explain why it is empty.
  // noApplicableCriteria: the form has criteria, but none of them apply under the audit's standards
  // (a different empty state than "no questions yet")
  renderEmptyState?: (noApplicableCriteria?: boolean) => JSX.Element | null;
  // The evidence tasks bound to a one-time check, keyed by lower-cased criterionKey, plus the handler
  // that opens one. Only an audit execution task passes these.
  evidenceTasksByCriterionKey?: Map<string, IEvidenceTaskLinkItem[]>;
  criterionBadgesByKey?: Map<string, string[]>;
  onOpenEvidenceTask?: (taskId: number) => void;
  //forwarded to the renderer: the names of the controls whose checks this form asks, for the
  //headings that split the questions per control
  entityNamesByKey?: Map<string, string>;
  // The elements this form just loaded. A caller that has to reason about the form itself - whether
  // it carries generated criteria, say - has no other way to know: it never reads the form.
  onFormLoaded?: (elements: TaskTypeFormElement[]) => void;
  // Set when the auditor took over a whole AI result on an audit execution task: every question of
  // that audited item carrying an AI answer is filled with it, so adopting a verdict does not have to
  // be repeated question by question. A NEW object is what applies it - the request-token shape
  // showErrors uses - so taking over the same result twice works too.
  takeOverAiAnswers?: { requestId: string; entity: AuditEntityKey };
  onUpdateData: (data: KPIData) => void;
  onSetData: (data: KPIData | KPIData[], instances?: Task[]) => void;
  onUpdateTaskForForm: (task: Task, validateForm: boolean) => void;
  addLinks: (links: ResourceLink[], validateForm: boolean) => void;
  setLinks: (links: ResourceLink[], validateForm: boolean) => void;
  removeLink: (link: ResourceLink, validateForm: boolean) => void;
  onValidate: (errors: TaskTypeFormElement[]) => void;
  onExitFormMode?: () => void;
  onClickFinish?: () => void;
}

//save the promise to local variable while loading async so that other async functions can wait for it
//-needed for validating the form async
//-put this outside of the component so this is not set on every re-render
let loadPromise: Promise<TaskTypeFormElement[]> | undefined;

//
// Expressions
//
const calcKPIExpressions = (task: Task, elements: TaskTypeFormElement[]): TaskTypeFormElement[] => {
  //calculate expressions
  if (!task.kpiData) return elements;
  const newElements = elements.map((e) => e.clone());

  const data = task.kpiData;
  const exprData = data.filter((e) => e.kpi?.type === KPITypes.Expression);

  exprData.forEach((e) => {
    if (e.kpi) {
      const result = KPI.calcExpression(task, e.kpi.defValue);

      //update the data on the task; a computed result is seeded by the form, not recorded by a person
      e.resultNumber = result; //this is used to save to back-end
      e.isSeeded = true;
      e.kpi.data[0].resultNumber = result; //this is used by the form
      e.kpi.data[0].isSeeded = true;

      //update the element on the form
      const elm = newElements.find((elm) => elm.kpiId === e.kpiId);
      if (elm && elm.kpi && elm.kpi.data.length > 0) {
        elm.kpi.data[0].resultNumber = result;
        elm.kpi.data[0].isSeeded = true;
      }
    }
  });

  return newElements;
};

//
// Default values
//
//Fills an empty data row with the KPI's default value and returns true when it set one.
//Runs once at load so the task and the form hold the same row; the form elements only render it.
//A row filled here is marked seeded: the server reads it as "not a recorded answer" until the user
//changes the value (onUpdateValue clears the mark).
const applyDefaultValue = (kpi: KPI, data: KPIData): boolean => {
  const seeded = seedDefaultValue(kpi, data);
  if (seeded) data.isSeeded = true;

  return seeded;
};

const seedDefaultValue = (kpi: KPI, data: KPIData): boolean => {
  const defValue = kpi.defValue ?? '';
  if (isEmpty(defValue)) return false;

  switch (kpi.type) {
    case KPITypes.Text:
    case KPITypes.TextMultiline:
    case KPITypes.Html:
      if (data.resultText) return false;
      data.resultText = defValue;

      return true;
    case KPITypes.Number: {
      if (data.resultNumber !== undefined) return false;
      const defVal = Number.parseFloat(defValue);
      if (isNaN(defVal)) return false;
      data.resultNumber = defVal;

      return true;
    }
    case KPITypes.Choice: {
      if (data.resultNumber !== undefined) return false;
      const defVal = Number.parseInt(defValue);
      if (isNaN(defVal)) return false;
      data.resultText = kpi.choices?.find((c) => c.stringValueKey === defVal)?.value;
      data.resultNumber = defVal;

      return true;
    }
    case KPITypes.Date:
      //a date field with a default value starts at the current date
      if (data.resultDate !== undefined) return false;
      data.resultDate = new Date();

      return true;
    default:
      return false;
  }
};

//
// Answers
//
//Whether a loaded field holds a recorded answer: a value the user put there, a comment or an
//attachment, the rule the server refuses an upgrade on. A row applyDefaultValue seeded at load and a
//computed expression result are not answers (KPIData.isRecordedAnswer); the server rule decides in
//the end and a refusal is shown as such
const formHasAnswers = (elements: TaskTypeFormElement[]): boolean =>
  elements.some((elm) => {
    const kpi = elm.kpi;
    if (!kpi || kpi.data.length === 0) return false;

    return kpi.data[0].isRecordedAnswer() || kpi.getAttachments().length > 0;
  });

const TaskTypeForm = (props: ITaskTypeFormProps) => {
  //task: is resolved here too (the full-screen Finish button), and namespaces load lazily: a key from
  //a namespace this list does not name renders as the raw key
  const { t } = useTranslation(['translation', 'forms', 'task']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formElements, setFormElements] = useState<TaskTypeFormElement[]>([]);
  const [taskType, setTaskType] = useState<TaskType | undefined>(undefined);
  const [taskId, setTaskId] = useState<number>(0);
  const [hasFormFeature] = useState<boolean>(hasUserFeature(appContext, FeatureTypes.TaskForm));
  const [editForm, setEditForm] = useState<TaskType | undefined>(undefined);
  const [isUpgrading, setIsUpgrading] = useState<boolean>(false);
  const theme = getTheme();

  const topTaskButtonStyles = {
    root: {
      color: theme.palette.themeSecondary,
    },
  };

  useEffect(() => {
    //load data also when form is not open
    //only load when task or form changes
    if (
      props.task &&
      props.task.taskTypeId &&
      (props.task.taskTypeId !== taskType?.taskTypeId || props.task.taskId !== taskId)
    ) {
      //resolves a generated (hidden) form too, which the cache cannot: without this the editor
      //opened on form 0 and loaded nothing
      setTaskType(
        TaskType.forTask(props.task.taskTypeId, appContext, t, props.task.taskTypeFamilyId, props.task.formVersion),
      );
      setTaskId(props.task.taskId);
      startLoadData();
    } else if (formElements.length > 0) {
      const elements = calcKPIExpressions(props.task, formElements);
      setFormElements(elements);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.task]);

  useEffect(() => {
    if (props.showErrors) {
      validateForm();
    } else {
      formElements.forEach((elm) => (elm.errorMessage = undefined));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.showErrors]);

  useEffect(() => {
    if (props.takeOverAiAnswers) {
      takeOverAiAnswers(props.takeOverAiAnswers.entity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.takeOverAiAnswers]);

  //
  // Load
  //
  const loadData = async (): Promise<TaskTypeFormElement[]> => {
    try {
      if (isLoading || !props.task.taskTypeId) {
        return formElements;
      }

      setIsLoading(true);
      appContext.showContentLoader();

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      let elements = props.loadFormAndData
        ? await props.loadFormAndData(accessToken)
        : await apiGetTaskTypeFormAndData(props.task.taskTypeId, EntityTypes.Task, props.task.taskId, accessToken);
      elements.sort((a, b) => sortOnNumber(a.sortOrder, b.sortOrder));

      //load the data from the task when there is data
      //otherwise get the data from the API
      //a form can only contain unique KPI's so we can find on criterium kpiId only
      const dataToSetOnTask: KPIData[] = [];

      elements.forEach((elm) => {
        if (elm.kpi) {
          const dataFromTask = props.task.kpiData?.find((d) => d.kpiId === elm.kpiId);
          if (dataFromTask) {
            //set unsaved data from the task
            elm.kpi.data = [dataFromTask];
            //update the KPI ref because the loaded data has another ref
            elm.kpi.data[0].kpi = elm.kpi;
          } else if (elm.kpi.data.length === 0) {
            //create a new empty data record
            const newData = new KPIData();
            newData.kpiId = elm.kpi.kpiId;
            elm.kpi.addData([newData]);

            if (elm.kpi.type === KPITypes.Expression) {
              //expressions cannot be set by the user
              //these records must therefore always be send to the task
              //other data records are send to the task when they are changed by the user
              dataToSetOnTask.push(newData);
            }
          } else {
            //there was data returned from the API
            //link the data record to the kpi
            //this is needed because the task object only contains KPIData objects
            //and to validate the KPIData objects, the KPI model is needed
            elm.kpi.data[0].kpi = elm.kpi;
            //push the data object to the task
            dataToSetOnTask.push(elm.kpi.data[0]);
          }

          //a default value is an answer like any other, so the task receives it here as well:
          //the form validates its own rows while the save request carries the rows of the task.
          //Only a row that was never saved is seeded, and only on a form that can be edited: a saved
          //row without a value is one the user cleared, and a completed task shows what was recorded,
          //not today's date in an empty Date field
          const data = elm.kpi.data[0];
          if (
            !props.readonly &&
            data.kpiDataId === 0 &&
            applyDefaultValue(elm.kpi, data) &&
            !dataToSetOnTask.includes(data)
          ) {
            dataToSetOnTask.push(data);
          }
        }
      });

      //when this form has expression fields that need subtask data, make sure it is loaded
      let instancesToUpdate: Task[] = [];
      const taskToCalcExpressions: Task = props.task.clone();
      const needSubTaskData = elements.some(
        (e) => e.kpi?.type === KPITypes.Expression && KPI.expressionNeedsSubtasks(e.kpi?.defValue),
      );
      if (needSubTaskData) {
        instancesToUpdate = await getSubTaskKPIData(props.task);
        if (!taskToCalcExpressions.instances) taskToCalcExpressions.instances = [];

        //build an id to index lookup once to avoid findIndex inside the loop
        const instanceIdxById = new Map<string | undefined, number>();
        taskToCalcExpressions.instances.forEach((t, idx) => instanceIdxById.set(t.id, idx));

        for (let idx = 0; idx < instancesToUpdate.length; idx++) {
          const newInstance = instancesToUpdate[idx];
          const currentInstanceIdx = instanceIdxById.get(newInstance.id);
          if (currentInstanceIdx !== undefined && currentInstanceIdx >= 0) {
            taskToCalcExpressions.instances[currentInstanceIdx] = newInstance;
          }
        }
      }

      //calculate expresions
      elements = calcKPIExpressions(taskToCalcExpressions, elements);

      //push changes to task
      props.onSetData(dataToSetOnTask, instancesToUpdate);

      //set state
      setFormElements(elements);
      props.onFormLoaded?.(elements);

      return elements;
    } catch (err) {
      appContext.setError(err);

      return formElements;
    } finally {
      appContext.hideContentLoader();
      setIsLoading(false);
    }
  };

  //Starts a load and publishes it for validateForm to wait on. The slot is cleared once the load
  //settles, and only while it is still this load's: clearing it inside loadData was too early for the
  //guarded early return above, which returns synchronously - so the slot was cleared BEFORE the
  //caller assigned this promise to it, and a resolved promise carrying the element list of that
  //moment stayed there for good. validateForm then validated (and put back) THAT list instead of
  //what is on screen, reporting errors for answers the auditor had since given.
  const startLoadData = (): void => {
    const promise = loadData();
    loadPromise = promise;
    void promise.finally(() => {
      if (loadPromise === promise) loadPromise = undefined;
    });
  };

  const getSubTaskKPIData = async (task: Task): Promise<Task[]> => {
    try {
      if (!task.instances || task.instances.length === 0) return [];

      const instancesToUpdate: Task[] = [];
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      //the instances are independent, so load their form data in parallel
      const instances = task.instances.map((i) => i.clone());
      const elementsPerInstance = await Promise.all(
        instances.map((instance) =>
          instance.taskTypeId
            ? apiGetTaskTypeFormAndData(instance.taskTypeId, EntityTypes.Task, instance.taskId, accessToken)
            : Promise.resolve(undefined),
        ),
      );

      for (let idx = 0; idx < instances.length; idx++) {
        const instance = instances[idx];
        const elements = elementsPerInstance[idx];
        if (instance.taskTypeId && elements) {
          if (!instance.kpiData) instance.kpiData = [];
          //track existing kpiIds in a set to avoid find inside the loop
          const existingKpiIds = new Set(instance.kpiData.map((d) => d.kpiId));
          let added = false;
          for (let elmIdx = 0; elmIdx < elements.length; elmIdx++) {
            const kpi = elements[elmIdx].kpi;
            if (kpi) {
              //when the data is not found in the instance, add it
              if (!existingKpiIds.has(kpi.kpiId) && kpi.data.length > 0) {
                instance.kpiData.push(kpi.data[0]);
                existingKpiIds.add(kpi.kpiId);
                if (!added) {
                  instancesToUpdate.push(instance);
                  added = true;
                }
              }
            }
          }
        }
      }

      return instancesToUpdate;
    } catch (err) {
      appContext.setError(err);

      return [];
    }
  };

  //
  // Validation
  //
  const validateForm = async () => {
    try {
      //when readonly, do not validate and let parent know that there are no errors
      //a form is readonly when the task is already completed in the saved state (so completing right now does validate) or when the task is a template/series type
      if (props.readonly) {
        props.onValidate([]);

        return;
      }

      //wait for the loadData to be finished, otherwise there is no data to validate
      let elements = formElements;
      if (loadPromise) {
        elements = await loadPromise;
        loadPromise = undefined;
      }

      //the elements are validated independently, so validate them in parallel
      const validationResults = await Promise.all(elements.map((elm) => validateValue(elm)));
      const errors = elements.filter((_, idx) => validationResults[idx]);

      setFormElements([...elements]);
      props.onValidate(errors);
    } catch (err) {
      appContext.setError(err);
    }
  };

  const validateValue = async (elm: TaskTypeFormElement): Promise<string | undefined> => {
    // Cleared before anything can return: a field that was in error when it stopped being validated -
    // its check archived, its evidence automated - would otherwise keep showing that error for good.
    elm.errorMessage = undefined;

    if (elm.kpi?.hasAutomatedEvidence) {
      return undefined;
    }

    // A retired criterion is never required: it is shown for what it was answered, and it is not
    // rendered at all when it was not. Deliberately NOT extended to every check-derived field - an
    // active check question is an ordinary question of this form and is validated as one.
    if (elm.retiredOn) {
      return undefined;
    }

    // a criterion outside the audit's standards is not asked on this task, so it is never required
    if (elm.notApplicable) {
      return undefined;
    }

    switch (elm.fieldType) {
      case TaskTypeFieldType.KPI:
        if (elm.kpi && elm.kpi.data.length > 0) {
          switch (elm.kpi.type) {
            case KPITypes.Choice:
            case KPITypes.Number:
            case KPITypes.SuccesError:
              if (elm.kpi.required && elm.kpi.data[0].resultNumber === undefined) {
                elm.errorMessage = t('forms:Errors.FieldRequired');
                break;
              }
              if (elm.kpi.minValue) {
                const min = Number.parseFloat(elm.kpi.minValue);
                if (elm.kpi.data[0].resultNumber === undefined || elm.kpi.data[0].resultNumber < min) {
                  elm.errorMessage = t('forms:Errors.NumberFieldTooShort', { value: min });
                  break;
                }
              }
              if (elm.kpi.maxValue) {
                const max = Number.parseFloat(elm.kpi.maxValue);
                if (elm.kpi.data[0].resultNumber !== undefined && elm.kpi.data[0].resultNumber > max) {
                  elm.errorMessage = t('forms:Errors.NumberFieldTooLong', { value: max });
                  break;
                }
              }
              break;
            case KPITypes.Date:
              if (elm.kpi.required && elm.kpi.data[0].resultDate === undefined) {
                elm.errorMessage = t('forms:Errors.FieldRequired');
                break;
              }
              break;
            case KPITypes.Html:
            case KPITypes.Text:
            case KPITypes.TextMultiline:
              if (elm.kpi.required && isEmpty(elm.kpi.data[0].resultText)) {
                elm.errorMessage = t('forms:Errors.FieldRequired');
                break;
              }
              if (elm.kpi.minValue) {
                const min = Number.parseInt(elm.kpi.minValue);
                if (
                  min > 0 &&
                  !isEmpty(elm.kpi.data[0].resultText) &&
                  elm.kpi.data[0].resultText &&
                  elm.kpi.data[0].resultText?.length < min
                ) {
                  elm.errorMessage = t('forms:Errors.TextFieldTooShort', {
                    length: min,
                    diff: min - (elm.kpi.data[0].resultText?.length ?? 0),
                  });
                  break;
                }
              }
              if (elm.kpi.maxValue) {
                const max = Number.parseInt(elm.kpi.maxValue);
                if (max > 0 && elm.kpi.data[0].resultText && elm.kpi.data[0].resultText?.length > max) {
                  elm.errorMessage = t('forms:Errors.TextFieldTooLong', {
                    length: max,
                    diff: elm.kpi.data[0].resultText?.length - max,
                  });
                  break;
                }
              }

              break;
          }
          if ((elm.kpi.attachmentMode ?? 0) > 0) {
            //there are required attachments
            const min = elm.kpi.getMinAttachments();
            const current = elm.kpi.getAttachments().length;
            if (current < min) {
              elm.errorMessage = t('forms:Errors.NotEnoughAttachments', { count: min - current });
            }
          }
          if ((elm.kpi.commentMode ?? 0) > 0) {
            //there is a required comment
            const min = elm.kpi.getMinComment();
            const current = elm.kpi.data[0].getCommentLength();
            if (current < min) {
              //a comment mode of 1 is "a comment is required" - what an audit criterion asks - and not
              //a minimum length. Counting characters against it asks for "1 more character" of an
              //empty box, and keeps asking it of a comment that is already there
              elm.errorMessage =
                min > 1
                  ? t('forms:Errors.NotEnoughComment', { count: min - current })
                  : t('forms:Errors.CommentRequired');
            }
          }
          //the maximum counts visible characters like the minimum; the stored HTML is longer and has its own limit
          const maxComment = elm.kpi.getMaxComment();
          const commentLength = elm.kpi.data[0].getCommentLength();
          if (maxComment > 0 && commentLength > maxComment) {
            elm.errorMessage = t('forms:Errors.CommentTooLong', {
              length: maxComment,
              diff: commentLength - maxComment,
            });
          }
        } else {
          throw new AppError('validateValue: KPI or KPI data is not set');
        }
        break;
      case TaskTypeFieldType.ContextAsset:
      case TaskTypeFieldType.ContextControl:
      case TaskTypeFieldType.ContextObjective:
      case TaskTypeFieldType.ContextProcess:
      case TaskTypeFieldType.ContextRequirement:
      case TaskTypeFieldType.ContextRisk:
        if ((elm.fieldMode ?? 0) > 0) {
          //there are required items
          const min = elm.getMinCount();
          const current = await elm.getCurrentContextCount(props.task, appContext);
          if (current < min) {
            elm.errorMessage = t('forms:Errors.NotEnoughContext', { count: min - current });
          }
        }
        break;
      case TaskTypeFieldType.AttachmentDocument:
      case TaskTypeFieldType.AttachmentListItem:
      case TaskTypeFieldType.AttachmentPage:
      case TaskTypeFieldType.AttachmentURL:
        if ((elm.fieldMode ?? 0) > 0) {
          //there are required items
          const links = await loadLinksForValidation(elm, props.links);
          const min = elm.getMinCount();
          const current = elm.getAttachments(links).length;
          if (current < min) {
            elm.errorMessage = t('forms:Errors.NotEnoughAttachments', { count: min - current });
          }
        }
        break;
    }

    return elm.errorMessage;
  };

  const loadLinksForValidation = async (elm: TaskTypeFormElement, links: ResourceLink[]): Promise<ResourceLink[]> => {
    try {
      if (!props.task) return [];
      if (props.task.resourceLinkIds && props.task.resourceLinkIds.length > links.length) {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const attachments = await apiGetLinksForId(accessToken, props.task.resourceLinkIds, appContext.globalDataCache);
        const listType = elm.getListTypeOfAttachmentFieldType();

        return attachments.filter((a) => a.list.listType === listType);
      } else {
        return links;
      }
    } catch (err) {
      appContext.setError(err);

      return [];
    }
  };

  //
  // Update helpers
  //

  // The auditor took over a whole AI result: the AI's answer is filled into the human answer of every
  // question of that audited item that has one - exactly what the per-question take-over does
  // (SingleFormElementAiVerdict), for the whole item at once, because adopting a verdict and then
  // re-answering its checks by hand is the same work twice. Like that one it writes into the EDITOR
  // only: the host's save (an autosave on the audit panel) is what stores it.
  const takeOverAiAnswers = (entity: AuditEntityKey) => {
    if (props.readonly) return;

    const entityKey = auditEntityKeyString(entity.entityType, entity.entityId);
    const newElements = [...formElements];
    const adopted: KPIData[] = [];

    newElements.forEach((elm, idx) => {
      const kpi = elm.kpi;
      const data = kpi?.data[0];
      if (!kpi || !data || elm.fieldType !== TaskTypeFieldType.KPI) return;
      //the two the per-question take-over refuses as well: a retired question is not answered any
      //more, and an automated one is not the auditor's to answer at all
      if (elm.retiredOn || elm.notApplicable || kpi.hasAutomatedEvidence) return;
      //the questions of THIS audited item only: a task auditing several holds the questions of each
      //of them, and this result is the verdict on one
      if (elm.entityType === undefined || elm.entityId === undefined) return;
      if (auditEntityKeyString(elm.entityType, elm.entityId) !== entityKey) return;
      //the AI was there but gave no answer this question could hold: nothing to adopt
      if (!data.hasAiAnswer()) return;

      //adopting the verdict is the auditor's decision: the row is a recorded answer from here on
      data.resultNumber = data.aiResultNumber;
      data.resultText = data.aiResultText;
      data.resultDate = data.aiResultDate;
      data.isSeeded = false;

      //cloning the KPI is what re-renders the field; the data record stays the one it holds
      const newKPI = kpi.clone();
      newKPI.data[0] = data;
      newKPI.data[0].kpi = newKPI;
      newElements[idx].kpi = newKPI;
      adopted.push(data);
    });

    if (adopted.length === 0) return;

    setFormElements(newElements);

    // One task update for all of them, instead of onUpdateData per answer: the hosts rebuild the task
    // from the state of the render this ran in, so a call per answer would keep the last one only.
    const newTask = props.task.clone();
    if (!newTask.kpiData) newTask.kpiData = [];
    adopted.forEach((data) => {
      const dataIdx = newTask.kpiData!.findIndex((row) => row.kpiId === data.kpiId);
      if (dataIdx >= 0) newTask.kpiData![dataIdx] = data.clone();
      else newTask.kpiData!.push(data.clone());
    });

    props.onUpdateTaskForForm(newTask, true);
  };

  const updateKPI = (kpi: KPI) => {
    const idx = formElements.findIndex((e) => e.kpiId === kpi.kpiId);
    if (idx >= 0) {
      const newElements = [...formElements];
      newElements[idx].kpi = kpi;
      setFormElements(newElements);
      if (kpi.data.length > 0) {
        props.onUpdateData(kpi.data[0]);
      }
    }
  };

  const onUpdateField = (elm: TaskTypeFormElement) => {
    const newElements = formElements.map((e) => (e.key === elm.key ? elm : e));
    setFormElements(newElements);
  };

  const onUpdateValue = (kpi: KPI, data: KPIData) => {
    //every form element reports a changed value here: whatever the form seeded, the user has now
    //made the value their own
    data.isSeeded = false;
    const newKPI = kpi.clone();
    if (newKPI.data.length > 0) {
      newKPI.data[0] = data;
      newKPI.data[0].kpi = newKPI;
      updateKPI(newKPI);
    }
  };

  const onUpdateKPI = (kpi: KPI) => {
    updateKPI(kpi);
  };

  const onSetKPI = (kpi: KPI) => {
    const idx = formElements.findIndex((e) => e.kpiId === kpi.kpiId);
    if (idx >= 0) {
      const newElements = [...formElements];
      newElements[idx].kpi = kpi;
      setFormElements(newElements);
      if (kpi.data.length > 0) {
        props.onSetData(kpi.data[0]);
      }
    }
  };

  const onUpdateTaskForForm = (task: Task) => {
    props.onUpdateTaskForForm(task, true);
  };

  // The edited form forked into a new version. A task without a recorded answer follows the fork in
  // the modal: the save then asks the server for the head of the task's own family, which it moves
  // like an upgrade (defaults re-seeded, recorded on the family). The rows go, the way the upgrade
  // drops them: the new version seeds its own defaults. A task with an answer keeps its version (the
  // server would refuse the move), so only the cache learns the new head.
  const onFormVersionChanged = (newTaskType: TaskType) => {
    appContext.globalDataCache.taskTypes.update(newTaskType);
    if (newTaskType.taskTypeId === props.task.taskTypeId) {
      setTaskType(newTaskType);

      return;
    }

    if (formHasAnswers(formElements)) return;

    setTaskType(newTaskType);
    const newTask = props.task.clone();
    newTask.taskTypeId = newTaskType.taskTypeId;
    newTask.formVersion = newTaskType.clone();
    newTask.kpiData = undefined;
    props.onUpdateTaskForForm(newTask, false);
  };

  //
  // Update to the latest form version
  //
  //The current head of this task's form family, when this consumer task may move to it: a saved, open
  //task that is not a series master, an event header or an audit execution task, with no answer
  //entered yet, for a user who may change its form. The cache holds the heads of the active families
  const getNewerFormVersion = (): TaskType | undefined => {
    //the same gate as the server: a task that is not in a workable state (completed, archived,
    //rejected) keeps its version, so the message is not shown for it
    if (
      props.task.taskId === -1 ||
      props.readonly ||
      !props.task.isInWorkableState() ||
      props.task.isSeries() ||
      props.task.isAuditExecutionTask()
    ) {
      return undefined;
    }

    const canUpdateTask = hasTaskUpdatePermission(props.task, appContext);
    if (!canUpdateTaskField(props.task, canUpdateTask, TaskFieldTypes.Form, appContext)) return undefined;

    const head = appContext.globalDataCache.taskTypes.getHead(props.task.taskTypeId, taskType?.taskTypeFamilyId);
    if (!head || head.taskTypeId === props.task.taskTypeId) return undefined;
    if (isLoading || formHasAnswers(formElements)) return undefined;

    return head;
  };

  const upgradeFormVersion = async () => {
    try {
      setIsUpgrading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const newTaskType = await apiUpgradeTaskFormVersion(props.task.taskId, accessToken);
      //the cache holds the heads of the active families: the head this task moved to is the newest
      //the server knows, so a fork from another session is taken over here as well
      appContext.globalDataCache.taskTypes.update(newTaskType);
      //only the task is re-pinned: the load effect then resolves the new version and reloads the form
      //the way a task open does, and SingleTask.onUpdateTaskForForm moves the saved state along.
      //onFormVersionChanged is not used here: it sets taskType first, so that effect sees no change.
      //The rows go too: the server re-seeded the defaults from the new version, and the reload reads
      //a row from the task before it reads the server, so the task must not carry the old ones
      const newTask = props.task.clone();
      newTask.taskTypeId = newTaskType.taskTypeId;
      newTask.formVersion = newTaskType.clone();
      newTask.kpiData = undefined;
      props.onUpdateTaskForForm(newTask, false);
      appContext.showNotification(t('forms:Versioning.Upgrade.TaskUpdated'), false, false, true);
    } catch (err) {
      //a refusal names its reason; the task keeps its pin, so the form is read back to show the server
      //state (a colleague answered or completed the task since it was opened)
      const refusal = getFormVersionConflictText(getFormVersionConflictCode(err), t);
      if (refusal) {
        appContext.showNotification(refusal, true, false, true);
        void loadData();
      } else {
        appContext.setError(err);
      }
    } finally {
      setIsUpgrading(false);
    }
  };

  const renderNewerVersionBar = (): JSX.Element | null => {
    const head = getNewerFormVersion();
    if (!head) return null;

    return (
      <IPStack.Item>
        <IPMessageBar
          messageBarType={IPMessageBarType.info}
          isMultiline={true}
          actions={
            <div>
              <IPMessageBarButton disabled={isUpgrading} onClick={() => void upgradeFormVersion()}>
                {t('forms:Versioning.Upgrade.UpdateTask')}
              </IPMessageBarButton>
            </div>
          }
        >
          {t('forms:Versioning.Upgrade.NewerVersionAvailable', { current: taskType?.version, latest: head.version })}
        </IPMessageBar>
      </IPStack.Item>
    );
  };

  const onAddLinks = (links: ResourceLink[]) => {
    props.addLinks(links, true);
  };

  const onSetLinks = (links: ResourceLink[]) => {
    props.setLinks(links, true);
  };

  const onRemoveLink = (link: ResourceLink) => {
    props.removeLink(link, true);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (props.onExitFormMode) props.onExitFormMode();
    }
  };

  //
  // Main render
  //
  if (!props.isOpen || !props.task.taskTypeId || !hasFormFeature) {
    return null;
  }

  if (props.formMode && !props.compactTaskView) {
    // Render in full screen mode over the parent task
    return (
      <IPStack
        verticalFill
        onKeyDown={onKeyDown}
        styles={getTaskViewModalStackStylesFormMode(theme, appContext.isMobileView, appContext.useDarkMode)}
      >
        <IPStack.Item grow>
          <IPStack verticalFill tokens={globalStackTokensGapMedium} onKeyDown={onKeyDown}>
            <IPStack.Item>
              <IPStack horizontal wrap tokens={globalStackTokensGapMedium} verticalAlign="center">
                <IPStack.Item>
                  <IPText variant="xxLarge">{props.task.name}</IPText>
                </IPStack.Item>
                <IPStack.Item>
                  <IPCommandButton
                    autoFocus
                    key={'exit'}
                    text={t('forms:View.ExitFormMode')}
                    iconProps={{ iconName: 'View' }}
                    onClick={() => {
                      if (props.onExitFormMode) props.onExitFormMode();
                    }}
                  />
                </IPStack.Item>
                {!props.readonly && props.onClickFinish && props.task.taskId !== -1 && (
                  <IPStack.Item>
                    <IPDefaultButton
                      styles={topTaskButtonStyles}
                      iconProps={{ iconName: 'FavoriteStarFill' }}
                      text={t('task:TopTasks.Finish')}
                      onClick={() => {
                        if (props.onExitFormMode) props.onExitFormMode();
                        if (props.onClickFinish) props.onClickFinish();
                      }}
                    />
                  </IPStack.Item>
                )}
              </IPStack>
            </IPStack.Item>
            <IPStack.Item>
              <TaskTagList
                tags={appContext.globalDataCache.tags.getItemsForId(props.task.tagIds)}
                isLoading={isLoading}
                readOnly={true}
                addTagToTaskState={() => {}}
                removeTagFromTaskState={() => {}}
              />
            </IPStack.Item>
            <IPStack.Item>{getHtmlJSX(props.task.description, true, true)}</IPStack.Item>
            {renderNewerVersionBar()}
            <IPStack.Item grow>
              <TaskTypeFormRenderer
                task={props.task}
                links={props.links}
                formElements={formElements}
                isLoading={isLoading}
                allowEdit={false}
                compact={false}
                readonly={props.readonly}
                onUpdateKPI={onUpdateKPI}
                onSetKPI={onSetKPI}
                onUpdateValue={onUpdateValue}
                onUpdateTaskForForm={onUpdateTaskForForm}
                onUpdateField={onUpdateField}
                addLinks={onAddLinks}
                setLinks={onSetLinks}
                removeLink={onRemoveLink}
                hideEmptyFormText={props.hideEmptyFormText}
                renderEmptyState={props.renderEmptyState}
                evidenceTasksByCriterionKey={props.evidenceTasksByCriterionKey}
                criterionBadgesByKey={props.criterionBadgesByKey}
                onOpenEvidenceTask={props.onOpenEvidenceTask}
                entityNamesByKey={props.entityNamesByKey}
              />
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
      </IPStack>
    );
  } else {
    //normal render
    return (
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        {/* "Edit form" edits the procedure's SHARED form, so it must never appear on an audit
            execution task: one auditor's edit would change the questionnaire of every other audit
            that applies the same procedure. It used to be absent only because the execution task
            never passed allowEdit - this says it out loud. Keyed off internalAuditId, not
            taskKind, which is InternalAudit for the procedure TEMPLATES too, where editing the
            form is exactly what the user came to do. */}
        {props.allowEdit &&
          !props.task.isAuditExecutionTask() &&
          taskType &&
          hasUserDataPermission(appContext, [taskType.authSchemaId], AuthSchemaLineOperation.Update) && (
            <IPStack.Item>
              <IPActionButton
                iconProps={editIcon}
                text={t('forms:Panels.Edit.Title')}
                onClick={() => {
                  //an edit always targets the current version of the form: opened on the version this
                  //task is pinned to, a task behind the head would be refused ("historical version")
                  setEditForm(
                    appContext.globalDataCache.taskTypes.getHead(props.task.taskTypeId, taskType.taskTypeFamilyId) ?? taskType,
                  );
                }}
              />
            </IPStack.Item>
          )}
        {renderNewerVersionBar()}
        <IPStack.Item grow styles={{ root: { minHeight: 0 } }}>
          <TaskTypeFormRenderer
            task={props.task}
            links={props.links}
            formElements={formElements}
            isLoading={isLoading}
            allowEdit={false}
            compact={props.compact}
            readonly={props.readonly}
            onUpdateKPI={onUpdateKPI}
            onSetKPI={onSetKPI}
            onUpdateValue={onUpdateValue}
            onUpdateTaskForForm={onUpdateTaskForForm}
            onUpdateField={onUpdateField}
            addLinks={onAddLinks}
            setLinks={onSetLinks}
            removeLink={onRemoveLink}
            hideEmptyFormText={props.hideEmptyFormText}
            renderEmptyState={props.renderEmptyState}
            evidenceTasksByCriterionKey={props.evidenceTasksByCriterionKey}
            criterionBadgesByKey={props.criterionBadgesByKey}
            onOpenEvidenceTask={props.onOpenEvidenceTask}
            entityNamesByKey={props.entityNamesByKey}
          />
        </IPStack.Item>
        {editForm && (
          <TaskTypeFormModal
            taskType={editForm}
            isOpen={true}
            entityNamesByKey={props.entityNamesByKey}
            onFormVersionChanged={onFormVersionChanged}
            onClose={() => {
              setEditForm(undefined);
              loadData();
            }}
          />
        )}
      </IPStack>
    );
  }
};

export default TaskTypeForm;
